import { ShapePropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/shape-property-converter';
import { IShapeModel } from '../../../../Interface/shape-model.interface';
import { DEFAULT_RAW_FONTSIZE } from '../../constants';
import { CharacterTypes, OfficeNumberType } from '../../constants/character-type';

/**
 * 描述字体大小和项目符号默认间距之间的映射关系
 * 这个数据是在 office ppt 中不同 fontSize 的文本设置项目符号得出的
 * 在 ppt 中不同项目符号对应有不同的映射关系，主要是小中大三种
 */
const smallFontSizeToCharacterDistanceArray = [
    {
        minFontSize: 0,
        maxFontSize: 13,
        distance: 13.5,
    },
    {
        minFontSize: 13,
        maxFontSize: 19,
        distance: 22.5,
    },
    {
        minFontSize: 19,
        maxFontSize: 26,
        distance: 27,
    },
    {
        minFontSize: 26,
        maxFontSize: 34,
        distance: 36,
    },
    {
        minFontSize: 34,
        maxFontSize: 46,
        distance: 45,
    },
    {
        minFontSize: 46,
        maxFontSize: 57,
        distance: 54,
    },
    {
        minFontSize: 57,
        maxFontSize: 76,
        distance: 67.5,
    },
    {
        minFontSize: 76,
        maxFontSize: Infinity,
        distance: 90,
    },
];

const middleFontSizeToCharacterDistanceArray = [
    {
        minFontSize: 0,
        maxFontSize: 13,
        distance: 18,
    },
    {
        minFontSize: 13,
        maxFontSize: 19,
        distance: 27,
    },
    {
        minFontSize: 19,
        maxFontSize: 26,
        distance: 36,
    },
    {
        minFontSize: 26,
        maxFontSize: 34,
        distance: 40.5,
    },
    {
        minFontSize: 34,
        maxFontSize: 46,
        distance: 58.5,
    },
    {
        minFontSize: 46,
        maxFontSize: 57,
        distance: 72,
    },
    {
        minFontSize: 57,
        maxFontSize: 76,
        distance: 90,
    },
    {
        minFontSize: 76,
        maxFontSize: Infinity,
        distance: 108,
    },
];

const bigFontSizeToCharacterDistanceArray = [
    {
        minFontSize: 0,
        maxFontSize: 13,
        distance: 22.5,
    },
    {
        minFontSize: 13,
        maxFontSize: 19,
        distance: 31.5,
    },
    {
        minFontSize: 19,
        maxFontSize: 26,
        distance: 40.5,
    },
    {
        minFontSize: 26,
        maxFontSize: 34,
        distance: 45,
    },
    {
        minFontSize: 34,
        maxFontSize: 46,
        distance: 67.5,
    },
    {
        minFontSize: 46,
        maxFontSize: 57,
        distance: 81,
    },
    {
        minFontSize: 57,
        maxFontSize: 76,
        distance: 90,
    },
    {
        minFontSize: 76,
        maxFontSize: Infinity,
        distance: 108,
    },
];
const bigCharacterTypes = [
    OfficeNumberType.ea1JpnChsDbPeriod,
];
const middleCharacterTypes = [
    OfficeNumberType.arabicPeriod, OfficeNumberType.arabicParenBoth,
    OfficeNumberType.arabicParenR, OfficeNumberType.alphaUcPeriod,
    OfficeNumberType.circleNumDbPlain, OfficeNumberType.romanUcPeriod, OfficeNumberType.alphaLcPeriod,
];
/**
 * 根据项目符号类型获取 字体大小和项目符号默认间距之间的映射关系
 * 这个规律是从 office ppt 中总结出来的
 * 普通项目符号应用 smallFontSizeToCharacterDistanceArray
 * 数字项目符号中宽度比较小的应用 middleFontSizeToCharacterDistanceArray
 * 数字项目符号中宽度比较大的应用 bigFontSizeToCharacterDistanceArray
 * @param type
 * @returns
 */
function getFontSizeToCharacterDistanceArray(type?: string): Array<{
    minFontSize: number;
    maxFontSize: number;
    distance: number
}>  {
    if (type && bigCharacterTypes.indexOf(type) !== -1) {
        return bigFontSizeToCharacterDistanceArray;
    }
    if (type && middleCharacterTypes.indexOf(type) !== -1) {
        return middleFontSizeToCharacterDistanceArray;
    }
    return smallFontSizeToCharacterDistanceArray;
}

/**
 * 根据 fontSize 和上述映射关系计算出默认的项目符号间距
 * @param fontSize
 * @returns
 */
function getDefaultDistanceByFontSize(fontSize: number, type?: string): number {
    const fontSizeToCharacterDistanceArray = getFontSizeToCharacterDistanceArray(type);
    return fontSizeToCharacterDistanceArray
        .find(({ minFontSize, maxFontSize }) => fontSize > minFontSize && fontSize <= maxFontSize)?.distance
      || fontSize;
}

interface ParagraphOption {
    // 当前段落所在的 shapeModel，用来获取继承数据
    shapeModel?: IShapeModel;
    // 原始的 fontSize，也就是保存的数据里面的 fontSize
    fontSize?: number;
    // 当前段落应用的 marginLeft 值
    marginLeft?: number;
    // 当前段落应用的 textIndent
    textIndent?: number;
    // 当前要添加的项目符号的类型，这个类型是 slide 中定义的
    characterType?: string;
    // 当前段落 level
    level?: number;
}

/**
 * 在设置项目符号时，判断是否需要使用继承的 marginLeft 和 textIndent
 * 这个逻辑是根据 office 表现推测的
 * 需要满足以下条件：
 * 1. 当前段落的 marginLeft + textIndent === 继承的 marginLeft + 继承的 textIndent
 * 2. 继承的数据中有项目符号
 * @param param0
 * @returns
 */
function needUseInheritCharacterDistance({
    shapeModel,
    level = 0,
    marginLeft = 0,
    textIndent = 0,
    characterType,
}: Pick<ParagraphOption, 'shapeModel' | 'level' | 'marginLeft' | 'textIndent' | 'characterType'>) {
    if (shapeModel && characterType) {
        // 获取当前 level 对应的继承来的项目符号相关数据
        const inheritCharacterRelated = ShapePropertyConverter.getInheritParagraphProps(shapeModel, level, ['marL', 'indent', 'character']);
        if (inheritCharacterRelated) {
            const {
                marginLeft: inheritMarginLeft = 0,
                textIndent: inheritTextIndent = 0,
                character: paragraphCharacter,
            } = inheritCharacterRelated;
            // 继承的数据中是否有项目符号
            const hasInheritCharacter = paragraphCharacter?.character && !paragraphCharacter.buNone;
            return (
                marginLeft + textIndent === inheritMarginLeft + inheritTextIndent
                && hasInheritCharacter
            );
        }
    }
    return false;
}
/**
 * 在设置项目符号时，根据当前段落数据计算项目符号的间距数据
 * 这个逻辑是之前按照 office 表现推测的
● 如果当前段落有 marginLeft 或 textIndent
	○ 如果有 textIndent 且为负值
		const diff = Math.abs(textIndent) - Math.abs(当前 fontSize 计算出来默认的 textIndent)
        ● 如果 diff >= 0，维持 marginLeft 和 textIndent 值不变
        ● 如果 diff < 0，最终 textIndent = textIndent - diff，marginLeft = marginLeft + diff
	○ 否则，最终 textIndent = 当前 fontSize 计算出来默认的 textIndent，
    marginLeft = marginLeft + 当前 fontSize 计算出来默认的 marginLeft
● 如果当前段落自身没设置 marginLeft 和 textIndent，直接按照 fontSize 计算
 * @param param0
 * @returns
 */
function calculateCharacterDistance({
    fontSize = DEFAULT_RAW_FONTSIZE,
    marginLeft = 0,
    textIndent = 0,
    characterType,
}: Pick<ParagraphOption, 'fontSize' | 'marginLeft' | 'textIndent' | 'characterType'>) {
    const distance = getDefaultDistanceByFontSize(fontSize, characterType);
    if (marginLeft || textIndent) {
        if (textIndent < 0) {
            const diff = Math.abs(textIndent) - distance;
            if (diff >= 0) {
                return {
                    marginLeft,
                    textIndent,
                };
            }
            return {
                marginLeft: marginLeft - diff,
                textIndent: textIndent + diff,
            };
        }
        if (marginLeft) {
            return {
                marginLeft: marginLeft + distance,
                textIndent: - distance,
            };
        }
    }
    return {
        marginLeft: distance,
        textIndent: -distance,
    };
}

/**
 * 设置项目符号时，计算新的 marginLeft 和 textIndent 并更新到传入的段落属性上
 * @param option
 * @param paragraphProps
 * @returns 返回修改后的 paragraphProps
 */
export function updateIndentationOnCharacterSet(option: ParagraphOption, paragraphProps) {
    if (needUseInheritCharacterDistance(option)) {
        const newParagraphProps = {
            ...paragraphProps,
        };
        delete newParagraphProps.marginLeft;
        delete newParagraphProps['raw-marginLeft'];
        delete newParagraphProps.textIndent;
        delete newParagraphProps['raw-textIndent'];
        return newParagraphProps;
    }
    return {
        ...paragraphProps,
        ...calculateCharacterDistance(option),
        'raw-marginLeft': true, // 加上raw-标志，用以提交sdk时区分继承属性
        'raw-textIndent': true, // 加上raw-标志，用以提交sdk时区分继承属性
    };
}

/**
 * 移除项目符号时，更新段落的缩进属性
 * 这个逻辑是根据 office 表现推测的
 * 计算之后的 marginLeft 或 textIndent 如果和继承的值一致，那么就删除自身的对应属性，使用继承的
 * @param option
 * @returns
 */
export function updateIndentationOnCharacterRemove(
    option: Pick<ParagraphOption, 'shapeModel' | 'level' | 'marginLeft' | 'textIndent'>,
    paragraphProps,
) {
    const { level = 0, shapeModel } = option;
    const { marginLeft, textIndent } = getMarginAndIndentWhenRemoveCharacter(option.marginLeft, option.textIndent);
    const newParagraphProps = {
        ...paragraphProps,
        marginLeft, textIndent,
        'raw-marginLeft': true, // 加上raw-标志，用以提交sdk时区分继承属性
        'raw-textIndent': true, // 加上raw-标志，用以提交sdk时区分继承属性
    };
    if (shapeModel) {
        const inheritIndentation = ShapePropertyConverter.getInheritParagraphProps(shapeModel, level, ['marL', 'indent']);
        if (inheritIndentation) {
            const { marginLeft: inheritMarginLeft, textIndent: InheritTextIndent } = inheritIndentation;
            if (inheritMarginLeft === marginLeft) {
                delete newParagraphProps.marginLeft;
                delete newParagraphProps['raw-marginLeft'];
            }
            if (InheritTextIndent === textIndent) {
                delete newParagraphProps.textIndent;
                delete newParagraphProps['raw-textIndent'];
            }
        }
    }
    return newParagraphProps;
}

function getMarginAndIndentWhenRemoveCharacter(marginLeft = 0, textIndent = 0) {
    /**
     * 看了下 office 的逻辑
     * - 会删除 indent
     * - marginLeft 会减去 indent 的大小，但不能小于 0
     */
    let newMarginLeft = marginLeft - Math.max(-textIndent, 0);

    // marginLeft不能为负数，否则会导致导出报修复
    newMarginLeft = Math.max(0, newMarginLeft);
    return {
        marginLeft: newMarginLeft,
        textIndent: 0,
    };
}

